import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import { UserContext } from "../contexts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Field,
  Icon,
  Input,
  StatePanel,
  Toggle,
  useToast,
} from "../components/ui";
import { waBroadcastApi, apiError } from "./waBroadcastApi";
import {
  RAMP_STEPS,
  atTime,
  canUseWaBroadcast,
  dayRangeLabel,
  nextStep,
  pad,
  formatPhone,
  fromNow,
  zoneDetail,
  zoneName,
} from "./waBroadcastHelpers";
import { WaAccountContext } from "./waAccount";
import { LinkDisclosure } from "./LinkDisclosure";
import { WaBroadcastAudience } from "./WaBroadcastAudience";
import { WaBroadcastActivity } from "./WaBroadcastActivity";
import { usePoll } from "../hooks/usePoll";

import "./wa-broadcast.scss";

const OVERVIEW_POLL_MS = 10000;
const LINK_POLL_MS = 3000;

/**
 * WhatsApp Broadcast page (store owners and admins of the active store).
 *
 * One page, top to bottom in the order someone needs it: is it safe and on →
 * link the number → who it posts to → how much → what happened. Every setting
 * saves the moment it changes; the only buttons are commands (link, refresh,
 * cancel, unlink). Everything below the number picker belongs to the selected
 * number — by default each person has their own; an owner on PLUS/PRO can
 * share one store number instead (Owner Admin → WhatsApp numbers).
 */
export function WaBroadcastSettings() {
  const { user, accessibleStores, activeStoreId } = useContext(UserContext);
  const toast = useToast();
  const allowed = canUseWaBroadcast(user, accessibleStores, activeStoreId);
  const [overview, setOverview] = useState(null);
  const [accountId, setAccountId] = useState(undefined);
  const [error, setError] = useState("");

  // `rethrow` only from the poll below, so usePoll sees the failure; Retry and
  // onLinked keep the old catch-and-show-inline behaviour.
  const load = useCallback(async ({ rethrow = false } = {}) => {
    try {
      const next = await waBroadcastApi.overview(accountId);
      setOverview(next);
      if (accountId === undefined) setAccountId(next.numbers?.selected_id);
      setError("");
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? "This build of the app is newer than the API — WhatsApp Broadcast is not deployed on the server yet."
          : apiError(err, "Could not load WhatsApp Broadcast.")
      );
      if (rethrow) throw err;
    }
  }, [accountId]);

  const onLinked = useCallback(() => {
    toast.success("WhatsApp number linked", {
      description: "Next: your groups load below by themselves — then save a list of the groups you want to post to.",
    });
    load();
  }, [toast, load]);

  // A different store means a different set of numbers.
  useEffect(() => {
    setOverview(null);
    setAccountId(undefined);
  }, [activeStoreId]);

  // usePoll (M4.1): every 10 s while healthy; backs off on errors, stops on
  // 403/404 (e.g. not deployed), pauses in a hidden tab. A store switch is a
  // new question, so it restarts the poll (and asks at once).
  const pollOverview = useCallback(() => load({ rethrow: true }), [load, activeStoreId]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(pollOverview, { interval: OVERVIEW_POLL_MS, enabled: allowed, immediate: true });

  if (!allowed) {
    return <StatePanel state="permission" headingLevel={2} />;
  }

  if (!overview) {
    return error ? (
      <StatePanel state="error" title="Could not load WhatsApp Broadcast" description={error} onRetry={load} />
    ) : (
      <StatePanel state="loading" title="Loading WhatsApp Broadcast…" loadingShape="card" />
    );
  }

  const connected = overview.account.status === "connected";
  const selected = overview.account.id;

  return (
    <WaAccountContext.Provider value={selected}>
      <div className="wa-broadcast wab-v2">
        {/* Numbers on the left, everything about the selected one on the right (Alex, 2026-10-23). */}
        <div className="wab-layout">
          <NumbersPanel
            numbers={overview.numbers}
            selected={selected}
            onSelect={(id) => {
              setOverview(null);
              setAccountId(id);
            }}
            onChanged={(next) => {
              // Added (now on the new number), renamed, or removed (back on the first).
              setOverview(next);
              setAccountId(next.numbers?.selected_id);
            }}
          />

          <div className="wab-main">
            <NumberHeader overview={overview} onChange={setOverview} />
            <NextStep overview={overview} />
            <KpiCards overview={overview} />

            {!connected && <LinkCard key={selected} overview={overview} onLinked={onLinked} />}

            <div className="wab-two">
              <WaBroadcastAudience key={`a${selected}`} connected={connected} />
              <LimitsCard overview={overview} onChange={setOverview} />
            </div>

            {/* Reloads whenever the counts move, so Activity keeps pace with "Today". */}
            <WaBroadcastActivity refreshKey={`${overview.usage?.today}/${overview.queued}`} />
          </div>
        </div>
      </div>
    </WaAccountContext.Provider>
  );
}

// ── The numbers: one card each (name, phone, live state), add / rename / remove ──

/** What a number is doing right now, in one word and a colour: green sends, red needs you. */
function numberState(a) {
  if (a.status === "connected") {
    if (a.paused) return { tone: "danger", label: a.queued ? `Off · ${a.queued} waiting` : "Off" };
    return { tone: "success", label: a.queued ? `Sending · ${a.queued} waiting` : "On" };
  }
  if (a.status === "unlinked") return { tone: "muted", label: "Not linked yet" };
  if (a.status === "linking") return { tone: "accent", label: "Linking…" };
  if (a.status === "restricted") return { tone: "danger", label: "Restricted" };
  return { tone: "danger", label: a.queued ? `Not linked · ${a.queued} waiting` : "Not linked" };
}

function NumbersPanel({ numbers, selected, onSelect, onChanged }) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [removeId, setRemoveId] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");
  // Naming a number: auto-saved while typing (no Save button), closed by Enter / Esc / leaving the box.
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState("");
  const saveTimer = useRef(null);

  if (!numbers) return null;
  const shared = numbers.mode === "shared";
  const accounts = numbers.accounts || [];
  // "Add" is hidden at the store's limit (or on an API without it). Otherwise it
  // stays tappable and, when it cannot add (sender box full), says why — a
  // disabled button's tooltip never shows on a phone.
  const showAdd = numbers.add_blocked_reason !== undefined && accounts.length < (numbers.max_per_store ?? Infinity);
  const blocked = numbers.add_blocked_reason;

  const saveName = async (id, value) => {
    try {
      onChanged(await waBroadcastApi.update({ label: value.trim() }, id));
    } catch (err) {
      toast.error(apiError(err, "Could not save the name."));
    }
  };
  const typeName = (id, value) => {
    setDraft(value);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveName(id, value), 700);
  };
  const closeName = (id, save) => {
    clearTimeout(saveTimer.current);
    const current = accounts.find((a) => a.id === id)?.label || "";
    if (save && draft.trim() !== current) saveName(id, draft);
    setEditId(null);
  };

  const add = async () => {
    if (blocked) {
      toast.info(blocked);
      return;
    }
    setAdding(true);
    try {
      onChanged(await waBroadcastApi.addNumber());
      toast.success("Number added", { description: "Link it on the right." });
    } catch (err) {
      toast.error(apiError(err, "Could not add a number."));
    } finally {
      setAdding(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    setRemoveError("");
    try {
      const next = await waBroadcastApi.removeNumber(removeId);
      setRemoveId(null);
      onChanged(next);
      toast.success("Number removed");
    } catch (err) {
      setRemoveError(apiError(err, "Could not remove the number."));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <aside className="wab-numpanel" aria-label="WhatsApp numbers">
      <span
        className="wab-numpanel__head"
        title={shared ? "Every admin of this store sends from these numbers" : "Only you can see and use these numbers"}
      >
        <Icon name={shared ? "Store" : "User"} size="xs" />
        {shared ? "Store numbers" : "Your numbers"}
      </span>

      <div className="wab-numpanel__list" role="radiogroup" aria-label="Number">
        {accounts.map((a) => {
          const active = a.id === selected;
          const phone = formatPhone(a.phone);
          const name = a.label || phone || "New number";
          const state = numberState(a);
          return (
            <div key={a.id} className={`wab-numcard ${active ? "is-active" : ""} is-${state.tone}`}>
              {editId === a.id ? (
                <input
                  className="wab-numcard__input"
                  aria-label={`Name for ${phone || "this number"}`}
                  placeholder="Name, e.g. Pierre"
                  maxLength={40}
                  autoFocus
                  value={draft}
                  onChange={(e) => typeName(a.id, e.target.value)}
                  onBlur={() => closeName(a.id, true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") closeName(a.id, true);
                    if (e.key === "Escape") closeName(a.id, false);
                  }}
                />
              ) : (
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={[name, a.label ? phone : null, state.label].filter(Boolean).join(", ")}
                  className="wab-numcard__pick"
                  onClick={() => !active && onSelect(a.id)}
                >
                  <span className="wab-numcard__name">{name}</span>
                  {a.label && phone && <span className="wab-numcard__phone">{phone}</span>}
                  <span className="wab-numcard__foot">
                    <span className={`wab-numcard__state is-${state.tone}`}>
                      <span className="wab-dot" aria-hidden="true" />
                      {state.label}
                    </span>
                    {a.status === "connected" && <span className="wab-numcard__today">{a.today ?? 0} today</span>}
                  </span>
                </button>
              )}
              {active && editId !== a.id && (
                <button
                  type="button"
                  className="wab-numcard__icon"
                  aria-label={`Name ${name}`}
                  title={a.label ? "Rename" : "Add a name"}
                  onClick={() => {
                    setDraft(a.label || "");
                    setEditId(a.id);
                  }}
                >
                  <Icon name="Pencil" size="sm" />
                </button>
              )}
              {a.removable && editId !== a.id && (
                <button
                  type="button"
                  className="wab-numcard__icon wab-numcard__remove"
                  aria-label={`Remove ${name}`}
                  title="Remove"
                  onClick={() => {
                    setRemoveError("");
                    setRemoveId(a.id);
                  }}
                >
                  <Icon name="X" size="sm" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showAdd ? (
        <>
          <Button
            variant="secondary"
            size="lg"
            className={`wab-numpanel__add ${blocked ? "is-blocked" : ""}`}
            loading={adding}
            leadingIcon={<Icon name="Plus" size="sm" />}
            aria-describedby={blocked ? "wab-add-blocked" : undefined}
            onClick={add}
          >
            Add number
          </Button>
          {blocked ? <span id="wab-add-blocked" hidden>{blocked}</span> : null}
        </>
      ) : (
        numbers.max_per_store != null && (
          <span className="wab-numpanel__note">{accounts.length} of {numbers.max_per_store} numbers in use</span>
        )
      )}

      <ConfirmDialog
        open={removeId !== null}
        onClose={() => setRemoveId(null)}
        onConfirm={remove}
        loading={removing}
        danger
        title="Remove this number?"
        description="Its groups, lists and history are deleted."
        confirmLabel="Remove"
      >
        {removeError ? <Alert tone="error" title={removeError} /> : null}
      </ConfirmDialog>
    </aside>
  );
}

// ── What to do next (stays until the number is fully set up) ────────────────

function NextStep({ overview }) {
  const step = nextStep(overview);
  // Setup only. "Turn it on" (step 4) is the red line with its Turn on button,
  // and a number that dropped out has its own red line and link form.
  const dropped = !["connected", "unlinked", "linking"].includes(overview.account.status);
  if (!step || step.n === 4 || dropped) return null;

  return (
    <div className="wab-next" role="status" aria-live="polite">
      <span className="wab-next__count">Step {step.n} of 4</span>
      <div className="wab-next__text">
        <strong>{step.title}</strong>
        <span>{step.text}</span>
      </div>
      <ol className="wab-next__dots" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
          <li key={i} className={i < step.n ? "is-done" : i === step.n ? "is-active" : ""} />
        ))}
      </ol>
    </div>
  );
}

// ── The selected number: name, connection, the kill switch, and what is wrong ──

function NumberHeader({ overview, onChange }) {
  const { account } = overview;
  const toast = useToast();
  const connected = account.status === "connected";
  const name = overview.numbers?.accounts?.find((a) => a.id === account.id)?.label;
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkError, setUnlinkError] = useState("");
  const [forget, setForget] = useState(false);
  const [turningOn, setTurningOn] = useState(false);

  // The sender has not answered the last sends; it restarts itself and retries.
  const stall = connected && !account.paused ? account.stall : null;
  const autoPaused = connected && account.paused && account.paused_reason && !/^Paused by |^Number unlinked/.test(account.paused_reason);
  const waiting = overview.queued || 0;
  const waitingText = waiting > 0 ? `${waiting} message${waiting === 1 ? "" : "s"} waiting` : "Nothing waiting";

  const toggleSending = async (next) => {
    try {
      const fresh = await waBroadcastApi.update({ paused: !next }, account.id);
      onChange(fresh);
      const queued = fresh.queued || 0;
      if (!next) {
        toast.info("Live Broadcasting is off", { description: "Nothing goes out until you switch it back on. Queued messages stay queued." });
      } else if (!fresh.enabled) {
        toast.warning("Live Broadcasting is on here, but switched off for the server", {
          description: "Messages queue but won't go out until Retail Circle switches the server on.",
        });
      } else {
        toast.success("Live Broadcasting is on", {
          description: queued > 0
            ? `${queued} queued message${queued === 1 ? "" : "s"} go out one at a time, 20–60 seconds apart.`
            : "Next: open a listing and choose “Share to Broadcast” to post it to your groups.",
        });
      }
    } catch (err) {
      throw new Error(apiError(err, "Couldn't save — try again"));
    }
  };

  const turnOn = async () => {
    setTurningOn(true);
    try {
      await toggleSending(true);
    } catch {
      toast.error("Couldn't switch it on — try again.");
    } finally {
      setTurningOn(false);
    }
  };

  const unlink = async () => {
    setUnlinking(true);
    setUnlinkError("");
    try {
      onChange(await waBroadcastApi.unlink(forget, account.id));
      toast.success("Number unlinked", {
        description: forget
          ? "Its groups, lists and history were deleted. Link a number again whenever you want to send."
          : "Its groups and lists are kept. Link a number again whenever you want to send.",
      });
      setConfirmUnlink(false);
      setForget(false);
    } catch (err) {
      setUnlinkError(apiError(err, "Could not unlink the number."));
    } finally {
      setUnlinking(false);
    }
  };

  // ONE red line when something needs a person, most important first; none when all is well.
  const problem = !overview.configured
    ? { title: "Not set up on the server yet.", text: "The number can't be linked until Retail Circle finishes the setup." }
    : account.status === "restricted"
      ? { title: "WhatsApp restricted this number.", text: account.paused_reason || "Stop for 7 days, then restart at week-1 volume." }
      : !connected && account.status !== "unlinked" && account.status !== "linking"
        ? { title: "This number is not linked.", text: `${waitingText} — they go out by themselves once it is linked again. Lists and history are kept.` }
        : connected && !overview.enabled
          ? { title: "Switched off for the server.", text: `${waitingText}. Nothing goes out until Retail Circle switches the server back on.` }
          : connected && account.paused
            ? {
                title: "Nothing is being sent.",
                text: autoPaused
                  ? `Paused ${fromNow(account.paused_at)}: ${account.paused_reason} · ${waitingText}.`
                  : `${account.paused_reason ? `${account.paused_reason} ${fromNow(account.paused_at)}` : "Live Broadcasting is off"} · ${waitingText}.`,
                action: true,
              }
            : stall
              ? {
                  title: `WhatsApp hasn't answered since ${fromNow(stall.since)}.`,
                  text: `${stall.tries} ${stall.tries === 1 ? "try" : "tries"} — the sender restarts itself and retries every 2 minutes. Nothing is lost.`,
                }
              : null;

  const status = connected
    ? { tone: "success", label: "Connected" }
    : account.status === "unlinked"
      ? { tone: "muted", label: "Not linked yet" }
      : account.status === "linking"
        ? { tone: "accent", label: "Linking…" }
        : { tone: "danger", label: account.status === "restricted" ? "Restricted" : "Not linked" };
  const on = connected && !account.paused;

  return (
    <section className="wab-head" aria-label="Number">
      <div className="wab-head__row">
        <div className="wab-head__who">
          <h2 className="wab-head__name rc-wab-heading">{name || formatPhone(account.phone) || "WhatsApp number"}</h2>
          <span className="wab-head__sub">
            {[name && formatPhone(account.phone), connected && overview.groups?.total ? `${overview.groups.total} groups` : connected ? "loading your groups" : null]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
        <span className={`wab-pill is-${status.tone}`} role="status">
          <span className="wab-dot" aria-hidden="true" />
          {status.label}
        </span>
        <div className={`wab-live ${connected && account.paused ? "is-off" : ""}`}>
          <span>{connected && account.paused ? "Live Broadcasting off" : "Live Broadcasting"}</span>
          <Toggle size="md" checked={on} disabled={!connected} onChange={toggleSending} aria-label="Live Broadcasting" />
        </div>
        {connected && (
          <Button variant="ghost" size="sm" className="wab-head__unlink" leadingIcon={<Icon name="LogOut" size="sm" />} onClick={() => setConfirmUnlink(true)}>
            Unlink
          </Button>
        )}
      </div>

      {problem && (
        <div className="wab-alert" role="status">
          <Icon name={problem.icon || "AlertTriangle"} size="md" />
          <p className="wab-alert__text">
            <strong>{problem.title}</strong> {problem.text}
          </p>
          {problem.action && (
            <Button size="md" loading={turningOn} onClick={turnOn}>Turn on</Button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmUnlink}
        onClose={() => setConfirmUnlink(false)}
        onConfirm={unlink}
        loading={unlinking}
        danger
        title="Unlink the WhatsApp number?"
        description="Sending stops, this device disappears from the phone's Linked devices, and the stored WhatsApp login is deleted from the sender. The phone itself is not affected. You can link it again any time."
        confirmLabel={forget ? "Unlink and forget" : "Unlink"}
      >
        <div className="wab-forget">
          <Checkbox
            checked={forget}
            onChange={(v) => setForget(v)}
            label="Also forget this number"
            description="Deletes its groups, saved lists and send history from Retail Circle. Use this after testing with a personal number."
          />
        </div>
        {unlinkError ? <Alert tone="error" title={unlinkError} /> : null}
      </ConfirmDialog>
    </section>
  );
}

// ── Four stat cards: today, this hour, the queue, and the sending hours ──────

function KpiCards({ overview }) {
  const { account, usage, limits } = overview;
  const win = limits.window;
  const connected = account.status === "connected";
  const sending = connected && !account.paused && overview.enabled;
  const noDailyLimit = limits.daily_cap == null;
  const hourCap = limits.hourly_cap || 40;
  const hourLeft = Math.max(0, hourCap - (usage.hour || 0));
  const dayLeft = noDailyLimit ? null : Math.max(0, limits.daily_cap - (usage.today || 0));
  const queued = overview.queued || 0;
  const zone = zoneDetail(win.timezone);

  const queueNote = queued === 0
    ? { text: "Nothing waiting" }
    : !connected
      ? { text: "Waiting for the number to be linked", tone: "danger" }
      : account.paused || !overview.enabled
        ? { text: "Waiting for Live Broadcasting", tone: "danger" }
        : !overview.in_window
          ? { text: "Goes out when sending hours open" }
          : sending && account.next_send_at
            ? { text: `Next message ${fromNow(account.next_send_at)}` }
            : { text: "Going out one at a time" };

  return (
    <div className="wab-kpis" aria-label="Sending">
      <div className="wab-kpi">
        <span className="wab-kpi__label"><Icon name="Calendar" size="xs" />Today</span>
        <span className="wab-kpi__value">{usage.today || 0} <small>{noDailyLimit ? "chats" : `of ${limits.daily_cap}`}</small></span>
        {!noDailyLimit && <span className="wab-kpi__bar" aria-hidden="true"><span style={{ width: `${Math.min(100, ((usage.today || 0) / (limits.daily_cap || 1)) * 100)}%` }} /></span>}
        <span className="wab-kpi__note">{noDailyLimit ? "No daily limit" : `${dayLeft} left today`}</span>
      </div>
      <div className="wab-kpi">
        <span className="wab-kpi__label"><Icon name="Activity" size="xs" />This hour</span>
        <span className="wab-kpi__value">{usage.hour || 0} <small>of {hourCap}</small></span>
        <span className="wab-kpi__bar" aria-hidden="true"><span style={{ width: `${Math.min(100, ((usage.hour || 0) / hourCap) * 100)}%` }} /></span>
        <span className="wab-kpi__note">{hourLeft} left this hour</span>
      </div>
      <div className="wab-kpi">
        <span className="wab-kpi__label"><Icon name="Inbox" size="xs" />Queue</span>
        <span className="wab-kpi__value">{queued} <small>waiting</small></span>
        <span className={`wab-kpi__note ${queueNote.tone ? `is-${queueNote.tone}` : ""}`}>{queueNote.text}</span>
      </div>
      <div className="wab-kpi">
        <span className="wab-kpi__label"><Icon name="Clock" size="xs" />Sending hours</span>
        <span className="wab-kpi__value">{pad(win.start_hour)}:00 – {pad(win.end_hour)}:00</span>
        <span className="wab-kpi__note">{dayRangeLabel(win.days)} · {zoneName(win.timezone)} time{zone ? ` (${zone})` : ""}</span>
        <span className={`wab-kpi__note ${overview.in_window ? "is-success" : ""}`}>
          {overview.in_window ? `Open now · closes ${pad(win.end_hour)}:00` : `Closed · opens ${atTime(overview.next_window_at)}`}
        </span>
      </div>
    </div>
  );
}

// ── Linking the number (pairing code: works from the phone itself) ───────────

function LinkCard({ overview, onLinked }) {
  const toast = useToast();
  const accountId = overview.account.id;
  const [disclosure, setDisclosure] = useState(false);
  const [phone, setPhone] = useState(overview.account.phone ? `+${overview.account.phone}` : "");
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  // null until the phone accepts the code; then { percent } while WhatsApp loads the account.
  const [syncing, setSyncing] = useState(null);
  const alive = useRef(true);
  // Read inside the status callback without re-creating it on every change.
  const inProgress = useRef(false);
  inProgress.current = !!(code || syncing);
  useEffect(() => () => { alive.current = false; }, []);

  // Apply one link-status reading: show the pending code, the sync progress, or finish.
  // Each step is announced once, however often the status is polled.
  const announced = useRef({});
  const announce = useCallback((key, show) => {
    if (announced.current[key]) return;
    announced.current[key] = true;
    show();
  }, []);

  const applyStatus = useCallback((res) => {
    if (!alive.current || !res) return;
    if (res.status === "connected") {
      announce("linked", onLinked);
    } else if (res.status === "syncing") {
      if (inProgress.current) {
        announce("accepted", () => toast.success("Phone accepted the code", {
          description: "WhatsApp is now loading the account. Keep this page open — it turns green by itself.",
        }));
      }
      setSyncing({ percent: res.percent ?? 0 });
    } else if (res.status === "awaiting_link" && res.pairing_code) {
      setCode(res.pairing_code);
    } else if (["not_started", "error", "auth_failed"].includes(res.status)) {
      // The sender restarted or gave up mid-link: drop the dead code / progress
      // and bring the form back, instead of polling a dead session forever.
      if (inProgress.current) setError("Linking stopped before it finished. Get a new code to try again.");
      setCode(null);
      setSyncing(null);
    }
  }, [onLinked, announce, toast]);

  // A refresh mid-link resumes where it is, instead of offering the empty form.
  useEffect(() => {
    waBroadcastApi.linkStatus(accountId).then(applyStatus).catch(() => {});
  }, [applyStatus, accountId]);

  // While a code is on screen or the account is loading, follow it to "connected".
  // usePoll (M4.1): every 3 s while healthy; backs off on errors, stops on
  // 403/404, pauses in a hidden tab. Rejects so the poll sees failures.
  const pollLink = useCallback(() => waBroadcastApi.linkStatus(accountId).then(applyStatus), [applyStatus, accountId]);
  usePoll(pollLink, { interval: LINK_POLL_MS, enabled: !!(code || syncing) });

  const digits = phone.replace(/\D/g, "");
  const phoneValid = digits.length >= 10 && digits.length <= 15;

  // "Get linking code" first shows the disclosure; the code is requested only
  // once every statement is ticked (the backend checks it was recorded).
  const getCode = async ({ accepted, read_messages }) => {
    setBusy(true);
    setError("");
    setCode(null);
    try {
      await waBroadcastApi.startLink(
        { version: overview.numbers?.terms_version, accepted, read_messages },
        accountId
      );
      setDisclosure(false);
      // The session needs a few seconds to reach WhatsApp's linking screen;
      // the service answers 409 until then, so keep asking briefly. A full
      // sender box is also a 409, but a final one.
      for (let attempt = 0; attempt < 15 && alive.current; attempt += 1) {
        try {
          const res = await waBroadcastApi.pairingCode(digits, accountId);
          if (alive.current) {
            setCode(res.code);
            toast.info("Your linking code is ready", {
              description: "On the phone: WhatsApp → Settings → Linked devices → Link a device → Link with phone number instead, then type the code.",
            });
          }
          return;
        } catch (err) {
          if (err?.response?.status !== 409 || err?.response?.data?.code === "sender_full") throw err;
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
      throw new Error("WhatsApp did not get ready in time. Try again in a minute.");
    } catch (err) {
      if (alive.current) {
        setDisclosure(false);
        setError(apiError(err, err?.message || "Could not get a linking code."));
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => alive.current && setCopied(false), 2000);
    } catch {
      setError("Could not copy — type the code in manually.");
    }
  };

  return (
    <Card className="wab-card wab-link" padding="lg">
      {/* Form (or the code) on the left; the steps it leads to on the right, always visible. */}
      <div className="wab-link__grid">
        <div className="wab-link__main">
          <h2 className="wab-title rc-wab-heading">Link a WhatsApp number</h2>
          <p className="wab-sub">
            Adds this service as a linked device, like WhatsApp Web. The phone keeps working normally.
          </p>

          {error && <Alert tone="error" title={error} />}

          {syncing ? (
            <div className="wab-link__code" role="status" aria-live="polite">
              <p className="wab-sync__title"><Icon name="CheckCircle2" size="sm" /> Phone linked — loading</p>
              <progress className="wab-bar" max={100} value={syncing.percent} aria-label={`Loading chats, ${syncing.percent}%`} />
              <p className="wab-hint">
                {syncing.percent}% · This can take a few minutes on a busy number. The page turns green by itself — keep it open or come back later.
              </p>
            </div>
          ) : !code ? (
            <div className="wab-link__form">
              <Field label="WhatsApp number" hint="With country code, e.g. +44 7507 785188">
                <Input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+44"
                  fullWidth
                />
              </Field>
              <Button
                size="lg"
                fullWidth
                loading={busy}
                disabled={!phoneValid || !overview.configured}
                leadingIcon={<Icon name="Phone" size="sm" />}
                onClick={() => setDisclosure(true)}
              >
                {busy ? "Opening WhatsApp…" : "Get linking code"}
              </Button>
            </div>
          ) : (
            <div className="wab-link__code">
              <span className="wab-code" aria-label={`Linking code ${code}`}>
                {code.slice(0, 4)}<span className="wab-code__dash">-</span>{code.slice(4)}
              </span>
              <div className="wab-link__actions">
                <Button variant="secondary" size="md" leadingIcon={<Icon name={copied ? "Check" : "Copy"} size="sm" />} onClick={copy}>
                  {copied ? "Copied" : "Copy code"}
                </Button>
                <Button variant="ghost" size="md" onClick={() => setCode(null)}>Use a different number</Button>
              </div>
              <p className="wab-hint"><Icon name="Clock" size="xs" /> Waiting for the phone… this page updates by itself.</p>
            </div>
          )}
        </div>

        <ol className="wab-steps" aria-label="How linking works">
          {[
            ["Enter the number", "The WhatsApp number you'll send from, with country code."],
            ["Get a code", "An 8-character code appears here."],
            ["Enter it on the phone", "WhatsApp → Settings → Linked devices → Link a device → Link with phone number instead."],
            ["Loading chats", "After the phone accepts, WhatsApp loads the account. This page turns green by itself."],
          ].map(([title, text], i) => {
            const active = syncing ? 3 : code ? 2 : busy ? 1 : 0;
            return (
              <li key={title} className={`wab-step ${i === active ? "is-active" : ""} ${i < active ? "is-done" : ""}`}>
                <span className="wab-step__num" aria-hidden="true">{i < active ? <Icon name="Check" size="xs" /> : i + 1}</span>
                <span className="wab-step__text">
                  <strong>{title}</strong>
                  <span>{text}</span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <LinkDisclosure
        open={disclosure}
        onClose={() => !busy && setDisclosure(false)}
        onAccept={getCode}
        accepting={busy}
      />
    </Card>
  );
}

// ── Daily limit (auto-saved) + the fixed safety rules ────────────────────────

function LimitsCard({ overview, onChange }) {
  const toast = useToast();
  const { limits } = overview;
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState("");

  const pick = async (value) => {
    if (value === limits.daily_cap || value > limits.daily_max) return;
    setSaving(value ?? "none");
    setError("");
    try {
      onChange(await waBroadcastApi.update({ daily_cap: value }, overview.account.id));
      if (value == null) {
        toast.info("No daily limit", {
          description: `Up to ${limits.hourly_cap} chats an hour, 20–60 s apart, inside sending hours. More messages a day means more risk of a WhatsApp ban — watch for warnings.`,
        });
      } else {
        toast.success(`Daily limit set to ${value} chats`, {
          description: "Raise it one step at a time, and only after a week with no warnings.",
        });
      }
    } catch (err) {
      setError(apiError(err, "Couldn't save the limit."));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card className="wab-card wab-limits" padding="lg">
      <div className="wab-card__head">
        <h2 className="wab-title rc-wab-heading">Daily limit</h2>
        <span className="wab-saved" aria-live="polite">{saving ? "Saving…" : ""}</span>
      </div>
      <div className="wab-ramp" role="radiogroup" aria-label="Chats per day">
        {RAMP_STEPS.filter((s) => s.value == null || s.value <= limits.daily_max).map((step) => {
          const active = limits.daily_cap === step.value;
          return (
            <button
              key={step.value ?? "none"}
              type="button"
              role="radio"
              aria-checked={active}
              className={`wab-ramp__step ${step.value == null ? "wab-ramp__step--none" : ""} ${active ? "is-active" : ""}`}
              onClick={() => pick(step.value)}
              disabled={saving != null}
            >
              <span className="wab-ramp__value">{step.display ?? step.value}</span>
              <span className="wab-ramp__label">{step.label}</span>
            </button>
          );
        })}
      </div>
      {!RAMP_STEPS.some((s) => s.value === limits.daily_cap) && (
        <p className="wab-hint">Currently {limits.daily_cap} per day.</p>
      )}
      {error && <Alert tone="error" title={error} />}

      {/* The fixed pace, and the rules Retail Circle Admin sets (live values). Sending hours have their own card. */}
      <dl className="wab-facts">
        <div><dt>Pace</dt><dd>{limits.gap_seconds[0]}–{limits.gap_seconds[1]} s apart, short breaks</dd></div>
        <div><dt>Per hour</dt><dd>At most {limits.hourly_cap}</dd></div>
        <div><dt>Per group</dt><dd>{limits.per_group_daily == null ? "No daily limit" : `At most ${limits.per_group_daily} a day`}</dd></div>
        <div>
          <dt>Repeats</dt>
          <dd>{limits.repeat_days == null ? "Same item can go again any time" : `Not within ${limits.repeat_days} day${limits.repeat_days === 1 ? "" : "s"}`}</dd>
        </div>
      </dl>
    </Card>
  );
}

export default WaBroadcastSettings;
